/**
 * Overlay Editor Dialog Component
 *
 * Dialog for editing a single overlay element (MVP: minimal fields).
 */

import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ColorPicker } from './components/ColorPicker';
import type { OverlayElement } from '../types';

interface OverlayEditorProps {
  overlay: OverlayElement | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (overlay: OverlayElement) => void;
}

export function OverlayEditor({ overlay, open, onOpenChange, onSave }: OverlayEditorProps) {
  const [overlayType, setOverlayType] = useState<'image' | 'text' | 'button'>('text');
  const [id, setId] = useState<string>('');
  const [content, setContent] = useState<string>('');
  const [assetRef, setAssetRef] = useState<string>('');
  const [href, setHref] = useState<string>('');
  const [color, setColor] = useState<string | undefined>(undefined);
  const [xPct, setXPct] = useState<number>(50);
  const [yPct, setYPct] = useState<number>(50);
  const [scale, setScale] = useState<number>(1);
  const [rotationDeg, setRotationDeg] = useState<number>(0);
  const [zIndex, setZIndex] = useState<number>(10);

  // Initialize form when overlay changes
  useEffect(() => {
    if (overlay) {
      setOverlayType(overlay.type);
      setId(overlay.id);
      setContent(overlay.content || '');
      setAssetRef(overlay.assetRef || '');
      setHref(overlay.href || '');
      setColor(overlay.color);
      setXPct(overlay.transform.xPct);
      setYPct(overlay.transform.yPct);
      setScale(overlay.transform.scale);
      setRotationDeg(overlay.transform.rotationDeg);
      setZIndex(overlay.transform.zIndex);
    } else {
      // New overlay: generate ID
      setId(`overlay-${Date.now()}`);
      setOverlayType('text');
      setContent('');
      setAssetRef('');
      setHref('');
      setColor(undefined);
      setXPct(50);
      setYPct(50);
      setScale(1);
      setRotationDeg(0);
      setZIndex(10);
    }
  }, [overlay]);

  const handleSave = () => {
    if (!id) {
      alert('ID is required');
      return;
    }

    const newOverlay: OverlayElement = {
      id,
      type: overlayType,
      transform: {
        xPct,
        yPct,
        scale,
        rotationDeg,
        zIndex,
      },
    };

    if (overlayType === 'image') {
      newOverlay.assetRef = assetRef || undefined;
    } else {
      newOverlay.content = content || undefined;
      if (overlayType === 'button') {
        newOverlay.href = href || undefined;
      }
      // Add color for text and button overlays
      if (color) {
        newOverlay.color = color;
      }
    }

    onSave(newOverlay);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{overlay ? 'Edit Overlay' : 'Add Overlay'}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Type</Label>
            <Select value={overlayType} onValueChange={(v) => setOverlayType(v as 'image' | 'text' | 'button')}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="text">Text</SelectItem>
                <SelectItem value="image">Image</SelectItem>
                <SelectItem value="button">Button</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>ID</Label>
            <Input
              value={id}
              onChange={(e) => setId(e.target.value)}
              placeholder="overlay-1"
            />
          </div>

          {overlayType === 'image' ? (
            <div className="space-y-2">
              <Label>Image URL</Label>
              <Input
                value={assetRef}
                onChange={(e) => setAssetRef(e.target.value)}
                placeholder="https://example.com/image.jpg"
              />
            </div>
          ) : (
            <div className="space-y-2">
              <Label>Content</Label>
              <Input
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder="Text content"
                maxLength={500}
              />
            </div>
          )}

          {overlayType === 'button' && (
            <div className="space-y-2">
              <Label>Link URL</Label>
              <Input
                value={href}
                onChange={(e) => setHref(e.target.value)}
                placeholder="https://example.com"
              />
            </div>
          )}

          {(overlayType === 'text' || overlayType === 'button') && (
            <ColorPicker
              value={color}
              onChange={setColor}
            />
          )}

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>X Position (%)</Label>
              <Input
                type="number"
                min="0"
                max="100"
                value={xPct}
                onChange={(e) => setXPct(Number(e.target.value))}
              />
            </div>
            <div className="space-y-2">
              <Label>Y Position (%)</Label>
              <Input
                type="number"
                min="0"
                max="100"
                value={yPct}
                onChange={(e) => setYPct(Number(e.target.value))}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Scale</Label>
              <Input
                type="number"
                min="0.1"
                max="10"
                step="0.1"
                value={scale}
                onChange={(e) => setScale(Number(e.target.value))}
              />
            </div>
            <div className="space-y-2">
              <Label>Rotation (degrees)</Label>
              <Input
                type="number"
                min="0"
                max="360"
                value={rotationDeg}
                onChange={(e) => setRotationDeg(Number(e.target.value))}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Z-Index</Label>
            <Input
              type="number"
              min="0"
              max="999"
              value={zIndex}
              onChange={(e) => setZIndex(Number(e.target.value))}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave}>
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
